import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { BusinessDocumentsEnum, SignupParams } from 'app/components/operator/operator-signup/signup-params';
import { XpressUserProfile } from 'app/models';
import { OperatorService } from 'app/services';
import { MessageService } from 'primeng/api';
import { normalizeUploadedUrls, Utils } from 'app/utils';

@Component({
  selector: 'upload-document-modal',
  templateUrl: './upload-document-modal.component.html',
  styleUrls: ['./upload-document-modal.component.scss']
})
export class UploadDocumentModalComponent implements OnInit, OnDestroy {

  @Input() public showUploadDocumentsPopup: boolean = false;

  @Input()
  public profile: XpressUserProfile = new XpressUserProfile();

  @Output()
  public hideUploadModal = new EventEmitter<any>();

  @Output()
  public updateUserProfile = new EventEmitter<XpressUserProfile>();

  govIdProgressBar: number = 0;
  DTIorSECProgressBar: number = 0;
  BIRProgressBar: number = 0;
  LTFRBProgressBar: number = 0;
  BusinessPermitProgressBar: number = 0;

  govIdInterval: any;
  DTIorSECInterval: any;
  BIRInterval: any;
  LTFRBInterval: any;
  BusinessPermitInterval: any;

  govIdUploadError: boolean = false;
  DTIorSECUploadError: boolean = false;
  BIRUploadError: boolean = false;
  LTFRBUploadError: boolean = false;
  BusinessPermitUploadError: boolean = false;
  isDocumentDeleted: boolean = false;
  documentLink: string[] = [];
  private closeMode: 'none' | 'submit' | 'discard' = 'none';
  private discardRequestEmitted = false;
  private uploadGenerationByType: Partial<Record<BusinessDocumentsEnum, number>> = {};
  private deferredUploadSuccess: Partial<Record<BusinessDocumentsEnum, string[]>> = {};

  constructor(
      private operatorService: OperatorService,
      private messageService: MessageService
    ) {
  }

  ngOnInit(): void {
    this.setProgressBarIfExistingFiles();
  }

  ngOnDestroy(): void {
    if (this.closeMode === 'none') {
      this.closeMode = 'discard';
    }
    this.invalidateAllUploadCallbacks();
    this.clearUploadIntervals();
  }

  public setProgressBarIfExistingFiles() {
        if (this.profile.GovIdData.length > 0) {
            this.govIdProgressBar = 100;
        }
        if (this.profile.DTIorSECCertifData.length > 0) {
            this.DTIorSECProgressBar = 100;
        }
        if (this.profile.BIRCertifData.length > 0) {
            this.BIRProgressBar = 100;
        }
        if (this.profile.LTFRBCertifData.length > 0) {
            this.LTFRBProgressBar = 100;
        }
        if (this.profile.BusinessPermitData.length > 0) {
            this.BusinessPermitProgressBar = 100;
        }
    }

  onFileDropped(docType: BusinessDocumentsEnum, $event: any) {
    this.prepareFiles(docType, $event);
  }

  fileBrowseHandler(docType: BusinessDocumentsEnum, files: any) {
      this.prepareFiles(docType, files);
  }

  prepareFiles(docType: BusinessDocumentsEnum, files: any) {
      let filesArr: any[];
      filesArr = files?.target?.files ? Array.from(files.target.files) : Array.from(files);
      filesArr = filesArr.filter((uploadedFile: File) => SignupParams.ACCEPTED_FILE_TYPES.includes(uploadedFile.type));
      filesArr = filesArr.filter((uploadedFile: File) => uploadedFile.size <= SignupParams.MAX_UPLOAD_FILE_SIZE);
      filesArr = filesArr.slice(0, SignupParams.MAX_UPLOAD_FILES_COUNT);

      this.uploadDocuments(filesArr, docType);
  }

  deleteUpload(docType: BusinessDocumentsEnum, file: File) {
      let link = '';
      let fileIndex = -1;
      switch (docType) {
          case BusinessDocumentsEnum.GovernmentIssuedId:
              fileIndex = this.profile.GovIdData.indexOf(file);
              link = this.profile.GovIdUrls[fileIndex];
              this.profile.GovIdData.splice(fileIndex, 1);
              this.profile.GovIdUrls.splice(fileIndex, 1);
              if (!this.profile.GovIdData.length) {
                  this.govIdProgressBar = 0;
              }
              break;
          case BusinessDocumentsEnum.DTIorSECCertificate:
              fileIndex = this.profile.DTIorSECCertifData.indexOf(file);
              link = this.profile.DTIorSECCertifUrls[fileIndex];
              this.profile.DTIorSECCertifData.splice(fileIndex, 1);
              this.profile.DTIorSECCertifUrls.splice(fileIndex, 1);
              if (!this.profile.DTIorSECCertifData.length) {
                  this.DTIorSECProgressBar = 0;
              }
              break;
          case BusinessDocumentsEnum.BIRCertificate:
              fileIndex = this.profile.BIRCertifData.indexOf(file);
              link = this.profile.BIRCertifUrls[fileIndex];
              this.profile.BIRCertifData.splice(fileIndex, 1);
              this.profile.BIRCertifUrls.splice(fileIndex, 1);
              if (!this.profile.BIRCertifData.length) {
                  this.BIRProgressBar = 0;
              }
              break;
          case BusinessDocumentsEnum.LTFRBCertificate:
              fileIndex = this.profile.LTFRBCertifData.indexOf(file);
              link = this.profile.LTFRBCertifUrls[fileIndex];
              this.profile.LTFRBCertifData.splice(fileIndex, 1);
              this.profile.LTFRBCertifUrls.splice(fileIndex, 1);
              if (!this.profile.LTFRBCertifData.length) {
                  this.LTFRBProgressBar = 0;
              }
              break;
          case BusinessDocumentsEnum.BusinessPermit:
              fileIndex = this.profile.BusinessPermitData.indexOf(file);
              link = this.profile.BusinessPermitUrls[fileIndex];
              this.profile.BusinessPermitData.splice(fileIndex, 1);
              this.profile.BusinessPermitUrls.splice(fileIndex, 1);
              if (!this.profile.BusinessPermitData.length) {
                  this.BusinessPermitProgressBar = 0;
              }
              break;
      }
      this.documentLink.push(link);
  }

  cancelUpload(docType: BusinessDocumentsEnum) {
      this.bumpUploadGeneration(docType);

      switch (docType) {
          case BusinessDocumentsEnum.GovernmentIssuedId:
              clearInterval(this.govIdInterval);
              this.govIdUploadError = false;
              this.restoreSlotFromUrls(
                  this.profile.GovIdUrls,
                  progress => this.govIdProgressBar = progress,
                  files => this.profile.GovIdData = files
              );
              break;
          case BusinessDocumentsEnum.DTIorSECCertificate:
              clearInterval(this.DTIorSECInterval);
              this.DTIorSECUploadError = false;
              this.restoreSlotFromUrls(
                  this.profile.DTIorSECCertifUrls,
                  progress => this.DTIorSECProgressBar = progress,
                  files => this.profile.DTIorSECCertifData = files
              );
              break;
          case BusinessDocumentsEnum.BIRCertificate:
              clearInterval(this.BIRInterval);
              this.BIRUploadError = false;
              this.restoreSlotFromUrls(
                  this.profile.BIRCertifUrls,
                  progress => this.BIRProgressBar = progress,
                  files => this.profile.BIRCertifData = files
              );
              break;
          case BusinessDocumentsEnum.LTFRBCertificate:
              clearInterval(this.LTFRBInterval);
              this.LTFRBUploadError = false;
              this.restoreSlotFromUrls(
                  this.profile.LTFRBCertifUrls,
                  progress => this.LTFRBProgressBar = progress,
                  files => this.profile.LTFRBCertifData = files
              );
              break;
          case BusinessDocumentsEnum.BusinessPermit:
              clearInterval(this.BusinessPermitInterval);
              this.BusinessPermitUploadError = false;
              this.restoreSlotFromUrls(
                  this.profile.BusinessPermitUrls,
                  progress => this.BusinessPermitProgressBar = progress,
                  files => this.profile.BusinessPermitData = files
              );
              break;
      }
  }

  private restoreSlotFromUrls(
      urls: string[] | undefined,
      setProgress: (progress: number) => void,
      setData: (files: File[]) => void
  ): void {
      const existing = urls || [];
      if (existing.length) {
          setProgress(100);
          setData(existing.map((_, i) => new File([], `document-${i + 1}`)));
          return;
      }
      setProgress(0);
      setData([]);
  }

  deleteDocuments(links: string[]) {
    this.operatorService.deleteDocuments(links, this.messageService)
        .then(data => {
        });
  }

  uploadDocuments(files: File[], docType: BusinessDocumentsEnum) {
    if(this.documentLink.length > 0){
        this.deleteDocuments(this.documentLink);
    }
      switch (docType) {
          case BusinessDocumentsEnum.GovernmentIssuedId:
              if (!files || !files.length) {
                  this.govIdUploadError = true;
                  return;
              }
              this.profile.GovIdData = files;
              this.govIdUploadError = false;
              this.govIdProgressBar = 1;
              this.govIdInterval = setInterval(() => {
                  if (this.govIdProgressBar >= 90) {
                      clearInterval(this.govIdInterval);
                  } else {
                      this.govIdProgressBar += 5;
                  }
              }, 100);
              break;
          case BusinessDocumentsEnum.DTIorSECCertificate:
              if (!files || !files.length) {
                  this.DTIorSECUploadError = true;
                  return;
              }
              this.profile.DTIorSECCertifData = files;
              this.DTIorSECUploadError = false;
              this.DTIorSECProgressBar = 1;
              this.DTIorSECInterval = setInterval(() => {
                  if (this.DTIorSECProgressBar >= 90) {
                      clearInterval(this.DTIorSECInterval);
                  } else {
                      this.DTIorSECProgressBar += 5;
                  }
              }, 100);
              break;
          case BusinessDocumentsEnum.BIRCertificate:
              if (!files || !files.length) {
                  this.BIRUploadError = true;
                  return;
              }
              this.profile.BIRCertifData = files;
              this.BIRUploadError = false;
              this.BIRProgressBar = 1;
              this.BIRInterval = setInterval(() => {
                  if (this.BIRProgressBar >= 90) {
                      clearInterval(this.BIRInterval);
                  } else {
                      this.BIRProgressBar += 5;
                  }
              }, 100);
              break;
          case BusinessDocumentsEnum.LTFRBCertificate:
              if (!files || !files.length) {
                  this.LTFRBUploadError = true;
                  return;
              }
              this.profile.LTFRBCertifData = files;
              this.LTFRBUploadError = false;
              this.LTFRBProgressBar = 1;
              this.LTFRBInterval = setInterval(() => {
                  if (this.LTFRBProgressBar >= 90) {
                      clearInterval(this.LTFRBInterval);
                  } else {
                      this.LTFRBProgressBar += 5;
                  }
              }, 100);
              break;
          case BusinessDocumentsEnum.BusinessPermit:
              if (!files || !files.length) {
                  this.BusinessPermitUploadError = true;
                  return;
              }
              this.profile.BusinessPermitData = files;
              this.BusinessPermitUploadError = false;
              this.BusinessPermitProgressBar = 1;
              this.BusinessPermitInterval = setInterval(() => {
                  if (this.BusinessPermitProgressBar >= 90) {
                      clearInterval(this.BusinessPermitInterval);
                  } else {
                      this.BusinessPermitProgressBar += 5;
                  }
              }, 100);
              break;
      }

      const formData = new FormData();
      for (const file of files) {
          formData.append('formFiles', file);
      }

      const generation = this.nextUploadGeneration(docType);
      this.operatorService.uploadDocuments(formData, this.messageService)
          .then(data => {
              if (!this.isUploadCallbackActive(docType, generation)) {
                  return;
              }
              if (!data || data.Success === false) {
                  this.afterUploadError(docType);
                  this.messageService.add({
                      severity: 'error',
                      summary: 'Upload failed',
                      detail: data?.ErrorMessage || 'File was not accepted. Use .pdf, .jpg, .jpeg, or .png (max 5MB).'
                  });
                  return;
              }
              const urls = normalizeUploadedUrls(data.UploadedDocsUrls);
              if (!urls.length) {
                  this.afterUploadError(docType);
                  this.messageService.add({
                      severity: 'error',
                      summary: 'Upload failed',
                      detail: 'File was not accepted. Use .pdf, .jpg, .jpeg, or .png (max 5MB).'
                  });
                  return;
              }
              this.applyOrDeferUploadSuccess(docType, urls);
          })
          .catch(err => {
              if (!this.isUploadCallbackActive(docType, generation)) {
                  return;
              }
              this.afterUploadError(docType);
              throw err;
          });
  }

  private nextUploadGeneration(docType: BusinessDocumentsEnum): number {
      const next = (this.uploadGenerationByType[docType] || 0) + 1;
      this.uploadGenerationByType[docType] = next;
      return next;
  }

  private bumpUploadGeneration(docType: BusinessDocumentsEnum): void {
      this.uploadGenerationByType[docType] = (this.uploadGenerationByType[docType] || 0) + 1;
  }

  private invalidateAllUploadCallbacks(): void {
      Object.values(BusinessDocumentsEnum)
          .filter((value): value is BusinessDocumentsEnum => typeof value === 'number')
          .forEach(docType => this.bumpUploadGeneration(docType));
  }

  private isUploadCallbackActive(docType: BusinessDocumentsEnum, generation: number): boolean {
      return this.closeMode !== 'discard'
          && generation === (this.uploadGenerationByType[docType] || 0);
  }

  private applyOrDeferUploadSuccess(docType: BusinessDocumentsEnum, docUrls: string[]): void {
      if (this.discardRequestEmitted) {
          this.deferredUploadSuccess[docType] = docUrls;
          this.markUploadUiComplete(docType, docUrls);
          return;
      }
      this.afterUploadSuccess(docType, docUrls);
  }

  private markUploadUiComplete(docType: BusinessDocumentsEnum, docUrls: string[]): void {
      const files = docUrls.map((_, i) => new File([], `document-${i + 1}`));
      switch (docType) {
          case BusinessDocumentsEnum.GovernmentIssuedId:
              clearInterval(this.govIdInterval);
              this.govIdProgressBar = 100;
              this.govIdUploadError = false;
              this.profile.GovIdData = files;
              break;
          case BusinessDocumentsEnum.DTIorSECCertificate:
              clearInterval(this.DTIorSECInterval);
              this.DTIorSECProgressBar = 100;
              this.DTIorSECUploadError = false;
              this.profile.DTIorSECCertifData = files;
              break;
          case BusinessDocumentsEnum.BIRCertificate:
              clearInterval(this.BIRInterval);
              this.BIRProgressBar = 100;
              this.BIRUploadError = false;
              this.profile.BIRCertifData = files;
              break;
          case BusinessDocumentsEnum.LTFRBCertificate:
              clearInterval(this.LTFRBInterval);
              this.LTFRBProgressBar = 100;
              this.LTFRBUploadError = false;
              this.profile.LTFRBCertifData = files;
              break;
          case BusinessDocumentsEnum.BusinessPermit:
              clearInterval(this.BusinessPermitInterval);
              this.BusinessPermitProgressBar = 100;
              this.BusinessPermitUploadError = false;
              this.profile.BusinessPermitData = files;
              break;
      }
  }

  afterUploadSuccess(docType: BusinessDocumentsEnum, docUrls: string[]) {
      switch (docType) {
          case BusinessDocumentsEnum.GovernmentIssuedId:
              clearInterval(this.govIdInterval);
              this.profile.GovIdUrls = docUrls;
              this.profile.GovIdData = docUrls.map((_, i) => new File([], `document-${i + 1}`));
              this.govIdProgressBar = 100;
              this.govIdUploadError = false;
              break;
          case BusinessDocumentsEnum.DTIorSECCertificate:
              clearInterval(this.DTIorSECInterval);
              this.profile.DTIorSECCertifUrls = docUrls;
              this.profile.DTIorSECCertifData = docUrls.map((_, i) => new File([], `document-${i + 1}`));
              this.DTIorSECProgressBar = 100;
              this.DTIorSECUploadError = false;
              break;
          case BusinessDocumentsEnum.BIRCertificate:
              clearInterval(this.BIRInterval);
              this.profile.BIRCertifUrls = docUrls;
              this.profile.BIRCertifData = docUrls.map((_, i) => new File([], `document-${i + 1}`));
              this.BIRProgressBar = 100;
              this.BIRUploadError = false;
              break;
          case BusinessDocumentsEnum.LTFRBCertificate:
              clearInterval(this.LTFRBInterval);
              this.profile.LTFRBCertifUrls = docUrls;
              this.profile.LTFRBCertifData = docUrls.map((_, i) => new File([], `document-${i + 1}`));
              this.LTFRBProgressBar = 100;
              this.LTFRBUploadError = false;
              break;
          case BusinessDocumentsEnum.BusinessPermit:
              clearInterval(this.BusinessPermitInterval);
              this.profile.BusinessPermitUrls = docUrls;
              this.profile.BusinessPermitData = docUrls.map((_, i) => new File([], `document-${i + 1}`));
              this.BusinessPermitProgressBar = 100;
              this.BusinessPermitUploadError = false;
              break;
      }
  }

  afterUploadError(docType: BusinessDocumentsEnum) {
      switch (docType) {
          case BusinessDocumentsEnum.GovernmentIssuedId:
              clearInterval(this.govIdInterval);
              if (this.profile.GovIdUrls?.length) {
                  this.govIdUploadError = false;
                  this.restoreSlotFromUrls(
                      this.profile.GovIdUrls,
                      progress => this.govIdProgressBar = progress,
                      files => this.profile.GovIdData = files
                  );
              } else {
                  this.govIdProgressBar = 0;
                  this.govIdUploadError = true;
                  this.profile.GovIdData = [];
              }
              break;
          case BusinessDocumentsEnum.DTIorSECCertificate:
              clearInterval(this.DTIorSECInterval);
              if (this.profile.DTIorSECCertifUrls?.length) {
                  this.DTIorSECUploadError = false;
                  this.restoreSlotFromUrls(
                      this.profile.DTIorSECCertifUrls,
                      progress => this.DTIorSECProgressBar = progress,
                      files => this.profile.DTIorSECCertifData = files
                  );
              } else {
                  this.DTIorSECProgressBar = 0;
                  this.DTIorSECUploadError = true;
                  this.profile.DTIorSECCertifData = [];
              }
              break;
          case BusinessDocumentsEnum.BIRCertificate:
              clearInterval(this.BIRInterval);
              if (this.profile.BIRCertifUrls?.length) {
                  this.BIRUploadError = false;
                  this.restoreSlotFromUrls(
                      this.profile.BIRCertifUrls,
                      progress => this.BIRProgressBar = progress,
                      files => this.profile.BIRCertifData = files
                  );
              } else {
                  this.BIRProgressBar = 0;
                  this.BIRUploadError = true;
                  this.profile.BIRCertifData = [];
              }
              break;
          case BusinessDocumentsEnum.LTFRBCertificate:
              clearInterval(this.LTFRBInterval);
              if (this.profile.LTFRBCertifUrls?.length) {
                  this.LTFRBUploadError = false;
                  this.restoreSlotFromUrls(
                      this.profile.LTFRBCertifUrls,
                      progress => this.LTFRBProgressBar = progress,
                      files => this.profile.LTFRBCertifData = files
                  );
              } else {
                  this.LTFRBProgressBar = 0;
                  this.LTFRBUploadError = true;
                  this.profile.LTFRBCertifData = [];
              }
              break;
          case BusinessDocumentsEnum.BusinessPermit:
              clearInterval(this.BusinessPermitInterval);
              if (this.profile.BusinessPermitUrls?.length) {
                  this.BusinessPermitUploadError = false;
                  this.restoreSlotFromUrls(
                      this.profile.BusinessPermitUrls,
                      progress => this.BusinessPermitProgressBar = progress,
                      files => this.profile.BusinessPermitData = files
                  );
              } else {
                  this.BusinessPermitProgressBar = 0;
                  this.BusinessPermitUploadError = true;
                  this.profile.BusinessPermitData = [];
              }
              break;
      }
  }

  private hasPendingUploads(): boolean {
      const bars = [
          this.govIdProgressBar,
          this.DTIorSECProgressBar,
          this.BIRProgressBar,
          this.LTFRBProgressBar,
          this.BusinessPermitProgressBar
      ];
      return bars.some(bar => bar > 0 && bar < 100);
  }

  public updateFilesLinks() {
    if (this.discardRequestEmitted || this.closeMode !== 'none') {
      return;
    }
    if (this.hasPendingUploads()) {
      this.messageService.add({
          severity: 'warn',
          summary: 'Upload in progress',
          detail: 'Please wait for all files to finish uploading before submitting.'
      });
      return;
    }
    this.updateUserProfile.emit(this.profile);
    this.closeWith('submit');
  }

  public discardChanges(): void {
    this.requestDiscard();
  }

  public onDialogHide(): void {
    if (this.closeMode !== 'none') {
      return;
    }
    this.requestDiscard();
    setTimeout(() => {
      if (this.closeMode === 'none') {
        this.showUploadDocumentsPopup = true;
      }
    });
  }

  private requestDiscard(): void {
    if (this.closeMode !== 'none' || this.discardRequestEmitted) {
      return;
    }
    this.discardRequestEmitted = true;
    this.hideUploadModal.emit(true);
  }

  public clearDiscardRequest(): void {
    this.discardRequestEmitted = false;
    const deferred = this.deferredUploadSuccess;
    this.deferredUploadSuccess = {};
    Object.keys(deferred).forEach(key => {
      const docType = Number(key) as BusinessDocumentsEnum;
      const urls = deferred[docType];
      if (urls?.length) {
        this.afterUploadSuccess(docType, urls);
      }
    });
  }

  public confirmDiscard(): void {
    this.closeMode = 'discard';
    this.discardRequestEmitted = false;
    this.deferredUploadSuccess = {};
    this.invalidateAllUploadCallbacks();
    this.clearUploadIntervals();
  }

  private closeWith(mode: 'submit' | 'discard'): void {
    if (this.closeMode !== 'none') {
      return;
    }
    this.closeMode = mode;
    if (mode === 'discard') {
      this.invalidateAllUploadCallbacks();
      this.clearUploadIntervals();
    }
    this.hideUploadModal.emit(mode === 'discard');
    this.showUploadDocumentsPopup = false;
  }

  private clearUploadIntervals(): void {
      clearInterval(this.govIdInterval);
      clearInterval(this.DTIorSECInterval);
      clearInterval(this.BIRInterval);
      clearInterval(this.LTFRBInterval);
      clearInterval(this.BusinessPermitInterval);
  }

  protected readonly BusinessDocumentsEnum = BusinessDocumentsEnum;

    protected readonly Utils = Utils;
}
